/**
 * RM V3 · RM-F — Vehicle Master & Daily Availability contract.
 *
 * Read/reference model only. Nothing in this module (or in any module that
 * imports it) writes a task, task_stop, route, saved plan, driver assignment
 * or POS record. The daily driver reference on an availability record is a
 * DISPLAY association: it is never read to set `tasks.assigned_driver_id`,
 * never joins a route, and never triggers reassignment.
 *
 * F0 is preserved: before actual route start POS remains authoritative, and
 * after start the execution boundary is unchanged — RM-F participates in
 * neither decision. RM-C stays shadow-only; capacity here is informational and
 * must not alter an optimisation plan in this release.
 */

import { parseVehicleWindows, type VehicleWindow } from '@/lib/vehicleWindows';

/** Tenant pilot flag for the vehicle master. Default OFF. */
export const VEHICLE_MASTER_FLAG = 'routing.vehicle_master_v1';

/**
 * Accepts both feature-flag storage shapes used in this project: the flat
 * dotted key and the nested `routing.*` object.
 */
export function isVehicleMasterEnabled(featureFlags: unknown): boolean {
  const flags = (featureFlags ?? {}) as Record<string, unknown>;
  if (flags[VEHICLE_MASTER_FLAG] === true) return true;
  const routing = (flags.routing ?? {}) as Record<string, unknown>;
  return routing.vehicle_master_v1 === true;
}

export const VEHICLE_TYPES = ['van', 'truck', 'car', 'motorcycle', 'bicycle', 'other'] as const;
export type VehicleType = (typeof VEHICLE_TYPES)[number];

/**
 * LEGACY vehicle-master capacity units. Retained ONLY so historical vehicle
 * rows stay readable. The operational capacity measure is `service_stops` on
 * the service window (see `vehicleWindows.ts`); nothing here is authoritative
 * and nothing here is offered for new entry.
 */
export const LEGACY_CAPACITY_UNITS = ['orders', 'kg', 'm3', 'pieces'] as const;
export type CapacityUnit = (typeof LEGACY_CAPACITY_UNITS)[number];

export const AVAILABILITY_STATUSES = ['available', 'unavailable', 'limited'] as const;
export type AvailabilityStatus = (typeof AVAILABILITY_STATUSES)[number];

export const VEHICLE_TYPE_LABELS: Record<VehicleType, string> = {
  van: 'Van',
  truck: 'Truck',
  car: 'Car',
  motorcycle: 'Motorcycle',
  bicycle: 'Bicycle',
  other: 'Other',
};

export const LEGACY_CAPACITY_UNIT_LABELS: Record<CapacityUnit, string> = {
  orders: 'orders',
  kg: 'kg',
  m3: 'm³',
  pieces: 'pieces',
};


export const AVAILABILITY_LABELS: Record<AvailabilityStatus, string> = {
  available: 'Available',
  unavailable: 'Unavailable',
  limited: 'Limited',
};

export const VEHICLE_OWNERSHIP_TYPES = ['company_owned', 'driver_provided'] as const;
export type VehicleOwnershipType = (typeof VEHICLE_OWNERSHIP_TYPES)[number];

export const VEHICLE_OWNERSHIP_LABELS: Record<VehicleOwnershipType, string> = {
  company_owned: 'Company owned',
  driver_provided: 'Driver provided',
};

export interface VehicleDayRow {
  vehicleId: string;
  name: string;
  registrationNumber: string;
  vehicleType: VehicleType | string;
  /**
   * Corrected fleet model: a company-owned vehicle has its own independent
   * timeline and can serve any driver (or a guest shift) when free. A
   * driver-provided vehicle belongs permanently to `ownerDriverProfileId` and
   * is never allocated to anyone else.
   */
  ownershipType: VehicleOwnershipType | string;
  ownerDriverProfileId: string | null;

  legacyCapacityValue: number | null;
  legacyCapacityUnit: CapacityUnit | string | null;
  isActive: boolean;
  /**
   * RM-F.2 legacy day-only record. `null` = no legacy record (not "available").
   * Kept readable for compatibility; new records are time windows.
   */
  availabilityStatus: AvailabilityStatus | null;
  driverProfileId: string | null;
  availabilityNotes: string | null;
  /** RM-F.2 — time windows intersecting the selected tenant-local day. */
  windows: VehicleWindow[];
}


export interface VehicleDayView {
  tenantId: string;
  serviceDate: string;
  /** Tenant IANA timezone used for all tenant-local rendering/editing. */
  timezone: string;
  flagKey: string;
  flagEnabled: boolean;
  canManage: boolean;
  vehicles: VehicleDayRow[];
  generatedAt: string | null;
}

/** Normalises the `fleet_vehicle_day_view` RPC payload. */
export function parseVehicleDayView(payload: unknown): VehicleDayView | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, any>;
  const rows: VehicleDayRow[] = Array.isArray(p.vehicles)
    ? p.vehicles.map((v: any) => ({
        vehicleId: v.vehicle_id,
        name: v.name,
        registrationNumber: v.registration_number,
        vehicleType: v.vehicle_type,
        // Older payloads predate the ownership model; company-owned is the
        // safe default because it matches the previous behaviour.
        ownershipType: v.ownership_type ?? 'company_owned',
        ownerDriverProfileId: v.owner_driver_profile_id ?? null,
        // RM-F.3: legacy only. Accepts the new `legacy_capacity_*` keys and the
        // pre-RM-F.3 `capacity_*` keys; either way it is never authoritative.
        legacyCapacityValue: (() => {
          const raw = v.legacy_capacity_value ?? v.capacity_value;
          return raw === null || raw === undefined ? null : Number(raw);
        })(),
        legacyCapacityUnit: v.legacy_capacity_unit ?? v.capacity_unit ?? null,

        isActive: v.is_active === true,
        availabilityStatus: (v.availability_status ?? null) as AvailabilityStatus | null,
        driverProfileId: v.driver_profile_id ?? null,
        availabilityNotes: v.availability_notes ?? null,
        windows: parseVehicleWindows(v.windows),
      }))
    : [];

  return {
    tenantId: p.tenant_id,
    serviceDate: p.service_date,
    // Older payloads (pre RM-F.2) carry no timezone; UTC is the safe default.
    timezone: typeof p.timezone === 'string' && p.timezone ? p.timezone : 'UTC',
    flagKey: p.flag_key ?? VEHICLE_MASTER_FLAG,
    flagEnabled: p.flag_enabled === true,
    canManage: p.can_manage === true,
    vehicles: rows,
    generatedAt: p.generated_at ?? null,
  };
}


// ---------------------------------------------------------------------------
// Validation — mirrors the DB CHECK constraints so the UI fails fast.
// ---------------------------------------------------------------------------

/**
 * RM-F.3 — the vehicle master no longer captures capacity. Capacity is set per
 * availability/reservation time window (see `vehicleWindows.ts`).
 */
export interface VehicleInput {
  name: string;
  registrationNumber: string;
  vehicleType: string;
  isActive: boolean;
  /** Defaults to company owned when omitted (legacy callers). */
  ownershipType?: string;
  /** Required for, and only for, a driver-provided vehicle. */
  ownerDriverProfileId?: string | null;
}

export interface ValidationResult {
  ok: boolean;
  errors: Record<string, string>;
}

export function validateVehicleInput(
  input: VehicleInput,
  existing: Array<{ vehicleId?: string; registrationNumber: string }> = [],
  editingVehicleId?: string,
): ValidationResult {
  const errors: Record<string, string> = {};

  if (!input.name || !input.name.trim()) errors.name = 'Vehicle name is required.';
  const reg = (input.registrationNumber ?? '').trim();
  if (!reg) errors.registrationNumber = 'Registration number is required.';

  if (!(VEHICLE_TYPES as readonly string[]).includes(input.vehicleType)) {
    errors.vehicleType = 'Choose a vehicle type.';
  }

  // Mirrors the DB CHECK constraints on fleet_vehicles.
  const ownership = input.ownershipType ?? 'company_owned';
  if (!(VEHICLE_OWNERSHIP_TYPES as readonly string[]).includes(ownership)) {
    errors.ownershipType = 'Choose whether the vehicle is company owned or driver provided.';
  } else if (ownership === 'driver_provided' && !input.ownerDriverProfileId) {
    errors.ownerDriverProfileId = 'Select the driver who owns this vehicle.';
  }



  if (reg) {
    const clash = existing.some(
      (v) =>
        v.registrationNumber.trim().toLowerCase() === reg.toLowerCase() &&
        v.vehicleId !== editingVehicleId,
    );
    if (clash) errors.registrationNumber = 'This registration number is already used in this tenant.';
  }

  return { ok: Object.keys(errors).length === 0, errors };
}

// ---------------------------------------------------------------------------
// Completeness indicator (pilot readiness)
// ---------------------------------------------------------------------------

export interface FleetCompleteness {
  totalVehicles: number;
  activeVehicles: number;
  /** Active vehicles with an explicit availability record for the date. */
  availabilitySet: number;
  missingAvailability: number;
  /**
   * RM-F.3 (final) — active vehicles that have at least one time window for the
   * date but no `max_service_stops` on ANY of those windows. A vehicle with no
   * window at all is counted under `missingAvailability`, not here.
   */
  missingWindowServiceStops: number;

  /** 0–100 across active vehicles; 0 when there are no active vehicles. */
  percent: number;
  status: 'empty' | 'incomplete' | 'complete';
}

export function computeFleetCompleteness(rows: VehicleDayRow[]): FleetCompleteness {
  const total = rows.length;
  const active = rows.filter((r) => r.isActive);
  // RM-F.2: a vehicle counts as scheduled when it has at least one time window
  // for the date, or a preserved legacy day-only record.
  const availabilitySet = active.filter(
    (r) => (r.windows?.length ?? 0) > 0 || r.availabilityStatus !== null,
  ).length;
  // RM-F.3: capacity readiness is a property of the windows, never of the
  // vehicle. Legacy vehicle capacity is deliberately ignored here.
  const scheduled = active.filter((r) => (r.windows?.length ?? 0) > 0);
  const capacitySet = scheduled.filter((r) =>
    r.windows.some((w) => w.capacityValue !== null),
  ).length;
  const missingWindowServiceStops = scheduled.length - capacitySet;

  if (active.length === 0) {
    return {
      totalVehicles: total,
      activeVehicles: 0,
      availabilitySet: 0,
      missingAvailability: 0,
      missingWindowServiceStops: 0,
      percent: 0,
      status: total === 0 ? 'empty' : 'incomplete',
    };
  }

  const scored = active.length * 2;
  const earned = availabilitySet + capacitySet;
  const percent = Math.round((earned / scored) * 100);

  return {
    totalVehicles: total,
    activeVehicles: active.length,
    availabilitySet,
    missingAvailability: active.length - availabilitySet,
    missingWindowServiceStops,
    percent,
    status: percent === 100 ? 'complete' : 'incomplete',
  };
}


/**
 * LEGACY vehicle capacity for display only (history). Never authoritative.
 * A missing capacity is never rendered as 0.
 */
export function formatLegacyCapacity(value: number | null, unit: string): string {
  if (value === null) return 'Not set';
  const label = (LEGACY_CAPACITY_UNIT_LABELS as Record<string, string>)[unit] ?? unit;
  return `${value} ${label}`;
}


/** Availability for display. No record is "Not set", never "Available". */
export function formatAvailability(status: AvailabilityStatus | null): string {
  if (status === null) return 'Not set';
  return AVAILABILITY_LABELS[status] ?? status;
}

/**
 * Local `yyyy-MM-dd` for today. Duplicated from Route Management deliberately:
 * the standalone Fleet surface must not pull the Route Management chunk.
 */
export function fleetServiceDateToday(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}
